// Felles validering og konstanter for feedback-vedlegg (VIB-691).
//
// Skilt ut fra `use-feedback.ts` så funksjonen kan unit-testes uten å
// dra inn supabase-klienten (som krever env-variabler ved import-tid).

/** Maks antall vedlegg per feedback. */
export const FEEDBACK_MAX_ATTACHMENTS = 10;

/** Maks filstørrelse per vedlegg i bytes (20 MB) — speilet av bucket-grensen. */
export const FEEDBACK_MAX_ATTACHMENT_BYTES = 20 * 1024 * 1024;

/** Tillatte MIME-typer for vedlegg — må matche bucket allowed_mime_types. */
export const FEEDBACK_ALLOWED_MIME_TYPES = [
  "image/png",
  "image/jpeg",
  "image/jpg",
  "image/webp",
  "image/gif",
  "application/pdf",
] as const;

export type FeedbackAllowedMimeType = (typeof FEEDBACK_ALLOWED_MIME_TYPES)[number];

/**
 * Validerer et sett med vedlegg klient-side. Returnerer en feilmelding hvis
 * noe ikke er gyldig, ellers `null`. Edge function og bucket-policy gjør
 * server-side håndheving i tillegg.
 */
export function validateFeedbackAttachments(files: File[]): string | null {
  if (files.length > FEEDBACK_MAX_ATTACHMENTS) {
    return `Maks ${FEEDBACK_MAX_ATTACHMENTS} filer per feedback`;
  }
  for (const file of files) {
    if (file.size > FEEDBACK_MAX_ATTACHMENT_BYTES) {
      return `${file.name} er større enn 20 MB`;
    }
    if (
      !FEEDBACK_ALLOWED_MIME_TYPES.includes(file.type as FeedbackAllowedMimeType)
    ) {
      return `${file.name} har ikke støttet filtype (bilder eller PDF)`;
    }
  }
  return null;
}

/**
 * Lager en safe path for en feedback-vedleggsfil. Format:
 *   `${userId}/${timestamp}-${random}-${sanitizedName}`
 *
 * Filnavn sanitizes for å unngå at brukerens originalnavn skaper trøbbel
 * med signed URLs eller bucket-validering (mellomrom, æøå, special chars).
 */
export function buildFeedbackAttachmentPath(userId: string, file: File): string {
  const safeName = file.name
    .normalize("NFKD")
    .replace(/[^a-zA-Z0-9._-]/g, "_")
    .slice(0, 100);
  const rand = Math.random().toString(36).slice(2, 8);
  return `${userId}/${Date.now()}-${rand}-${safeName}`;
}
